import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { BtnWrapper } from "../../styles/Docs/DocsToolbar.styled";
import { useMediaQuery } from "react-responsive";
import ClearIcon from "@mui/icons-material/Clear";

import { CircularProgress } from "@mui/material";
import { AlertTooltip } from "./../Spreadsheet/ModTooltip";
import {
  ArchiveDownloadIcon,
  ArchiveUploadIcon,
  DoneIcon,
} from "../../../public/icons";
import {
  archiveDocs,
  ARCHIVE_RESET,
  unarchiveDocs,
  UNARCHIVE_RESET,
} from "../../actions/archive";
import { isDocSigning } from "../../helpers/docPermissions";

interface ArchiveIconProps {
  archive: {
    state: "idle" | "loading" | "success" | "failure";
  };
  isArchived: boolean;
  disable: boolean;
}

const ArchiveIcon = ({ archive, isArchived, disable }: ArchiveIconProps) => {
  if (archive.state === "loading") {
    return <CircularProgress sx={{ color: "var(--blue)" }} />;
  }
  if (archive.state === "success") {
    return <DoneIcon />;
  }
  if (archive.state === "failure") {
    return <ClearIcon sx={{ color: "var(--vivid-pink)" }} />;
  }
  return isArchived ? (
    <ArchiveDownloadIcon
      color={disable ? "var(--disabled-c)" : "var(--blue)"}
    />
  ) : (
    <ArchiveUploadIcon color={disable ? "var(--disabled-c)" : "var(--blue)"} />
  );
};

interface ArchiveButtonProps<T> {
  size?: number | string;
  bgColor?: string;
  doc?: T & { archived?: boolean };
  selectedDocs: string[];
  archived?: boolean;
  docsUpdate?: () => void;
}

export default function ArchiveButton<T>(props: ArchiveButtonProps<T>) {
  const dispatch = useAppDispatch();
  const archive = useAppSelector((state) => state.archive);
  const unarchive = useAppSelector((state) => state.unarchive);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const typeSelectedDocs = props.archived !== undefined ? props.archived : null;
  const type = typeSelectedDocs !== null ? typeSelectedDocs : props.doc?.archived;
  const operationState = type ? unarchive : archive;

  const onClose = () => {
    if (operationState.state === "failure") {
      setTimeout(() => setTooltipOpen(false), 2000);
    } else {
      setTooltipOpen(false);
    }
  };

  const disable =
    !props.selectedDocs?.length ||
    operationState.state === "loading" ||
    (props.doc ? isDocSigning(props.doc as any) : false);

  const getTooltipTitle = () => {
    if (operationState.state === "failure") return "Помилка архівування документа";
    if (operationState.state === "success")
      return type ? "Вилучено з архіву" : "Додано в архів";
    return type ? "Розархівувати" : "Архівувати";
  };

  useEffect(() => {
    if (operationState.state === "success" || operationState.state === "failure") {
      setTooltipOpen(true);
      const timer = setTimeout(() => {
        setTooltipOpen(false);
        dispatch({ type: type ? UNARCHIVE_RESET : ARCHIVE_RESET });
        typeSelectedDocs !== null && props.docsUpdate?.();
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [operationState.state, dispatch]);

  const onHover = () => {
    if (!isMobile) {
      setTooltipOpen(true);
    }
  };

  const handleArchive = () => {
    if (type) {
      dispatch(unarchiveDocs(props.selectedDocs));
    } else {
      dispatch(archiveDocs(props.selectedDocs));
    }
  };

  return (
    <React.Fragment>
      <AlertTooltip
        open={tooltipOpen}
        id={
          operationState.state === "failure"
            ? "failure"
            : operationState.state === "success"
              ? "success"
              : "idle"
        }
        title={getTooltipTitle()}
      >
        <span>
          <BtnWrapper
            $bgColor={
              operationState.state === "failure"
                ? "rgba(255, 0, 127, 0.15)"
                : (props.bgColor ?? "var(--sidebar-hover)")
            }
            $color={"var(--white)"}
            onClick={handleArchive}
            onMouseEnter={onHover}
            onMouseLeave={onClose}
            disabled={disable}
          >
            <ArchiveIcon
              isArchived={!!type}
              archive={operationState}
              disable={disable}
            />
          </BtnWrapper>
        </span>
      </AlertTooltip>
    </React.Fragment>
  );
}
